/**
 * OpenTelemetry bootstrap for the browser (CORE-142).
 *
 * MUST BE THE FIRST IMPORT IN main.ts. The XHR instrumentation works by
 * patching `XMLHttpRequest`, which Angular's `HttpClient` resolves through
 * `XhrFactory` at request time -- so patching before `bootstrapApplication()`
 * is early enough, but only just. An editor's "organize imports" will happily
 * sort this below `@angular/core` and the only symptom is that no API call ever
 * carries a `traceparent`, with nothing logged anywhere.
 *
 * ## This installs a real provider, unlike every backend in the fleet
 *
 * A backend with no endpoint configured can run on a no-op tracer and still be
 * useful, because it only has to PASS ON a trace id somebody upstream created.
 * The browser is where the trace STARTS. A no-op tracer produces an invalid
 * span context, there is no id to inject, and every backend trace begins as its
 * own root with no idea which click caused it -- which is most of the value of
 * tracing the front end at all.
 *
 * So the provider is always installed. What is NOT installed is an exporter.
 *
 * ## Why nothing is exported
 *
 * `alloy-otel.monitoring.svc.cluster.local` resolves inside the cluster and
 * nowhere else. A browser pointed at it fails DNS on every export, forever.
 * Browser spans need a publicly reachable OTLP endpoint with CORS configured,
 * and that does not exist yet.
 *
 * The supported state is therefore **propagate, do not export**: this app
 * creates the trace id and sends it on every API call, and the backends record
 * the spans. The trace is complete from the first backend hop onward; the
 * browser's own timing is not in it.
 *
 * Do NOT work around this by proxying OTLP through members-back-end. That puts
 * unauthenticated span data from untrusted clients onto an internal collector
 * and turns the API into a spam relay. The fix is an ingress for the collector,
 * which is a platform ticket.
 *
 * ## Privacy
 *
 * A span created here is generated on a member's machine. No PII in span names
 * or attributes, no query strings (they carry tokens and search terms), and no
 * baggage -- baggage propagates to every backend and lands in logs.
 */

import { context as otelContext, propagation, trace } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
    CompositePropagator,
    W3CBaggagePropagator,
    W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';

import { environment } from './environments/environment';
import { buildPropagationAllowList } from './otel-origins';

/**
 * Install the browser tracer. Never throws: a telemetry fault must not stop the
 * application from rendering.
 */
export function configureOtel(): void {
    try {
        const provider = new WebTracerProvider({
            resource: defaultResource().merge(
                resourceFromAttributes({
                    // Build-time, not runtime: a browser cannot read the pod's
                    // environment. See environments/env.types.ts.
                    'service.name': environment.OTEL_SERVICE_NAME,
                    'service.version': environment.version,
                    'deployment.environment.name': environment.production ? 'prod' : 'dev',
                }),
            ),
            // No span processors: nothing is exported. See the module comment.
            spanProcessors: [],
        });

        provider.register({
            // Angular runs inside Zone.js, and the default StackContextManager
            // loses the active span across every async boundary the zone owns
            // -- which is most of them. Without this, spans are created and are
            // all siblings of nothing, and an API call started from a component
            // does not nest under the user action that triggered it.
            contextManager: new ZoneContextManager(),
            propagator: new CompositePropagator({
                propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
            }),
        });

        registerInstrumentations({
            instrumentations: [
                // Angular's HttpClient uses XMLHttpRequest, not fetch, unless
                // provideHttpClient(withFetch()) is set -- it is not. There is
                // no axios or fetch call anywhere in src/, so the fetch
                // instrumentation would be dead weight in the bundle. Add it
                // the day either appears.
                new XMLHttpRequestInstrumentation({
                    // The set of origins we may send a traceparent to, derived
                    // from the API URLs the app already has. NOT optional: web
                    // instrumentation deliberately refuses to add the header
                    // cross-origin without it, because traceparent is not
                    // CORS-safelisted -- adding it turns a simple request into
                    // a preflighted one, and a backend that does not allow the
                    // header rejects the call outright rather than degrading.
                    // Leave this empty and everything configures cleanly,
                    // spans are created, and no backend ever sees a trace id.
                    //
                    // Both backends this app calls already allow it:
                    // core-svc-auth lists traceparent/tracestate explicitly
                    // (CORE-139), and members-back-end reflects requested
                    // headers through app.enableCors().
                    propagateTraceHeaderCorsUrls: buildPropagationAllowList([
                        environment.API_URL,
                        environment.CORE_SVC_AUTH_URL,
                    ]),
                    // Frees the browser's resource-timing buffer as spans are
                    // created; without it a long session slowly fills it and
                    // later timings are silently dropped.
                    clearTimingResources: true,
                }),
            ],
        });
    } catch (err) {
        // One line, not silent: "no trace ids are reaching the API" should be
        // answerable from the console rather than by reading this file.
        console.warn('otel_tracing status=failed', err);
        return;
    }

    console.info(
        'otel_tracing status=propagating service=' +
            environment.OTEL_SERVICE_NAME +
            ' export=none reason=no_public_collector',
    );
}

/** The active trace and span ids, for correlating a console error with a backend trace. */
export function currentTraceIds(): { traceId?: string; spanId?: string } {
    const spanContext = trace.getActiveSpan()?.spanContext();
    if (!spanContext) return {};
    return { traceId: spanContext.traceId, spanId: spanContext.spanId };
}

/** Re-exported so call sites import one module. */
export { otelContext, propagation, trace };

configureOtel();
